import { useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { WalletProvider, useWallet } from "@/context/WalletContext";
import { AppShell } from "@/components/layout/AppShell";

import ComingSoon from "@/pages/ComingSoon";
import Landing from "@/pages/Landing";
import PublicBurns from "@/pages/PublicBurns";
import MilestoneCard from "@/pages/MilestoneCard";
import Docs from "@/pages/Docs";
import Legal from "@/pages/Legal";
import Overview from "@/pages/app/Overview";
import Wizard from "@/pages/app/Wizard";
import Launches from "@/pages/app/Launches";
import LaunchDetail from "@/pages/app/LaunchDetail";
import ActivityPage from "@/pages/app/Activity";
import JuiceBurns from "@/pages/app/JuiceBurns";
import Broadcast from "@/pages/app/Broadcast";
import Integrations from "@/pages/app/Integrations";
import Billing from "@/pages/app/Billing";
import Telegram from "@/pages/app/Telegram";
import Receipt from "@/pages/app/Receipt";
import RosterAdmin from "@/pages/app/RosterAdmin";
import KolMagic from "@/pages/KolMagic";
import SettingsPage from "@/pages/app/Settings";

const RequireWallet = ({ children }) => {
  const { wallet } = useWallet();
  if (!wallet) return <Navigate to="/" replace />;
  return <AppShell>{children}</AppShell>;
};

// --- Coming Soon gate ---
// Everything is gated behind a "Coming Soon" page (frontend) AND the backend API
// (see coming_soon_gate middleware). Visit SECRET_PATH once to unlock both on this
// device. Flip COMING_SOON to false here AND in backend/server.py to go fully live.
const COMING_SOON = true;
const PREVIEW_KEY = "mj_preview_unlocked";
const PREVIEW_TOKEN = "mj-7x2q9v";
const SECRET_PATH = "/preview-mj-7x2q9v";
const isUnlocked = () => localStorage.getItem(PREVIEW_KEY) === "true";

function App() {
  const [unlocked, setUnlocked] = useState(isUnlocked());

  useEffect(() => {
    document.title =
      COMING_SOON && !unlocked
        ? "Wen Moon"
        : "Wen Moon — Stop asking wen. Launch it to the moon.";
  }, [unlocked]);

  const PreviewUnlock = () => {
    useEffect(() => {
      localStorage.setItem(PREVIEW_KEY, "true");
      localStorage.setItem("mj_preview_token", PREVIEW_TOKEN);
      setUnlocked(true);
    }, []);
    return <Navigate to="/" replace />;
  };

  if (COMING_SOON && !unlocked) {
    return (
      <div className="App">
        <BrowserRouter>
          <Routes>
            <Route path={SECRET_PATH} element={<PreviewUnlock />} />
            <Route path="*" element={<ComingSoon />} />
          </Routes>
        </BrowserRouter>
      </div>
    );
  }

  return (
    <div className="App">
      <WalletProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/burns" element={<PublicBurns />} />
            <Route path="/m/:id" element={<MilestoneCard />} />
            <Route path="/docs" element={<Docs />} />
            <Route path="/legal" element={<Legal />} />
            <Route path="/app" element={<RequireWallet><Overview /></RequireWallet>} />
            <Route path="/app/launch" element={<RequireWallet><Wizard /></RequireWallet>} />
            <Route path="/app/launches" element={<RequireWallet><Launches /></RequireWallet>} />
            <Route path="/app/launches/:id" element={<RequireWallet><LaunchDetail /></RequireWallet>} />
            <Route path="/app/activity" element={<RequireWallet><ActivityPage /></RequireWallet>} />
            <Route path="/app/burns" element={<RequireWallet><JuiceBurns /></RequireWallet>} />
            <Route path="/app/broadcast" element={<RequireWallet><Broadcast /></RequireWallet>} />
            <Route path="/app/telegram" element={<RequireWallet><Telegram /></RequireWallet>} />
            <Route path="/app/integrations" element={<RequireWallet><Integrations /></RequireWallet>} />
            <Route path="/app/billing" element={<RequireWallet><Billing /></RequireWallet>} />
            <Route path="/app/receipt/:id" element={<RequireWallet><Receipt /></RequireWallet>} />
            <Route path="/app/settings" element={<RequireWallet><SettingsPage /></RequireWallet>} />
            <Route path="/app/roster" element={<RequireWallet><RosterAdmin /></RequireWallet>} />
            <Route path="/kol/:token" element={<KolMagic />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster position="top-right" theme="dark" />
      </WalletProvider>
    </div>
  );
}

export default App;
